import { css, html, LitElement, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import { BoardDataStorage } from '../data-storage/data-storage.js'
import { DataSubscriptionProviderImpl } from '../graphql/data-subscription.js'

import { create, SCENE_MODE } from '@hatiolab/things-scene'

/**
 * @class BoardWrapper
 *
 * @description scene provider로부터 제공받은 scene의 reference count control을 담당하며, resize 이벤트 발생시 scene의 사이즈를 fit 시키는 역할을 담당한다.
 */
@customElement('ox-board-wrapper')
class BoardWrapper extends LitElement {
  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
      }

      #target {
        flex: 1;

        width: 100%;
        height: 100%;
        outline: 0;
      }
    `
  ]

  @property({ type: Object }) provider!: any
  @property({ type: Object }) data: any
  @property({ type: Object }) board?: any
  @property({ type: String }) baseUrl?: string

  private _scene: any
  private connected: boolean = false

  @query('#target') _targetEl!: HTMLElement

  render() {
    return html` <div id="target"></div> `
  }

  connectedCallback() {
    super.connectedCallback()

    this.connected = true

    window.addEventListener('resize', () => {
      requestAnimationFrame(() => {
        if (this._scene) {
          this._scene.resize()

          if (this.offsetWidth) {
            this._scene.fit()
          }
        }
      })
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.connected = false

    /* ox-board-player-carousel에 의해서 mutation 되면서, 잠깐 disconnected되므로,
    최종적으로 disconnected 여부를 확인하여야 한다. */
    requestAnimationFrame(() => {
      if (!this.connected) {
        this.closeScene()
      }
    })
  }

  updated(changes: PropertyValues<this>) {
    changes.has('board') && this._onBoardChanged()
  }

  _onBoardChanged() {
    this.closeScene()

    this.initScene()
  }

  initScene() {
    if (!this.board || !this.board.id) {
      return
    }

    var { model } = this.board

    try {
      if (typeof model == 'string') {
        model = JSON.parse(model)
      } else {
        model = JSON.parse(JSON.stringify(model))
      }
    } catch (err) {
      console.error(err)
    }

    this._scene = create({
      model,
      mode: SCENE_MODE.VIEW,
      refProvider: this.provider!,
      dataStorage: new BoardDataStorage(this.board.id),
      dataSubscriptionProvider: new DataSubscriptionProviderImpl(),
      handlers: ['animator-handler']
    })

    if (this.baseUrl) {
      this._scene.baseUrl = this.baseUrl
    }

    this.setupScene({ id: this.board.id, scene: this._scene })
  }

  closeScene() {
    if (this._scene) {
      this._scene.target = null
      this._scene.release && this._scene.release()

      this._scene = null
    }
  }

  setupScene({ id, scene }: { id: string; scene: any }) {
    this._scene = scene

    const backgroundColor = this._scene?.root.state.fillStyle
    if (typeof backgroundColor === 'string') {
      this.style.backgroundColor = backgroundColor
    } else {
      this.style.backgroundColor = 'initial'
    }

    this._scene.target = this._targetEl

    requestAnimationFrame(() => {
      if (this._scene?.target.offsetWidth) {
        this._scene.fit()
      }
    })

    if (this.data) {
      this._scene.data = this.data
    }
  }
}
